import { useLayoutEffect, useRef } from 'react'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Group from 'components/utils/Group'
import { useIntl, FormattedMessage } from 'react-intl'
import { getAuthoredTransferDetails } from 'lib/offer/getOfferTransferDetails'

interface Props {
  finePrint: string
  offer: App.Offer
  transferDetailsSectionId?: string
}

function FinePrint(props: Props) {
  const { finePrint, offer, transferDetailsSectionId } = props
  const intl = useIntl()
  const transferDetailsRef = useRef<HTMLDivElement>(null)
  const authoredTransferDetails = getAuthoredTransferDetails(offer)
  const transferDetailsLabel = intl.formatMessage({
    id: 'GXroK7lI26uoOSRlHnPz',
    defaultMessage: 'Important Information',
    description: 'Section heading for authored important information in the offer fine print modal',
  })

  useLayoutEffect(() => {
    if (!transferDetailsSectionId) return
    transferDetailsRef.current?.scrollIntoView({ block: 'start' })
  }, [transferDetailsSectionId])

  return (
    <Group direction="vertical" gap={20}>
      {authoredTransferDetails && (
        <Group ref={transferDetailsRef} id={transferDetailsSectionId} direction="vertical" gap={12}>
          <BodyText variant="medium" weight="bold">
            {authoredTransferDetails.heading || transferDetailsLabel}
          </BodyText>
          {!!authoredTransferDetails.body && (
            <MarkdownRender type="compact" fontSize="medium" content={authoredTransferDetails.body} />
          )}
        </Group>
      )}
      {!!offer.property?.taxesAndFeesContent &&
        <Group direction="vertical" gap={12}>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'ul64eBDVbL6AwfnpdKyt',
              defaultMessage: 'Taxes & fees:',
              description: 'Form field label for taxes and fees section in the fine print details section',
            })}
          </BodyText>
          <MarkdownRender type="compact" fontSize="medium" content={offer.property?.taxesAndFeesContent} />
        </Group>}

      {offer.type === OFFER_TYPE_ALWAYS_ON &&
        <Group direction="vertical" gap={12}>
          <BodyText variant="medium" weight="bold">{intl.formatMessage({
            id: 'knBEfB0uUHC7bbL8MKNA',
            defaultMessage: 'Cancellation Policy',
            description: 'Heading for the cancellation policy section in the cancellation flow',
          })}
          </BodyText>
          <BodyText variant="medium">
            <FormattedMessage
              id="FvyVjVL4R0Wz_fS2bBTM"
              defaultMessage="Please note cancellation policies vary depending on the rate selected, please choose your package carefully. See full <link>booking terms and conditions</link>"
              description="Note about varying cancellation policies with link to booking terms and conditions"
              values={{
                link: (chunks) => <TextLink weight="regular" to="/booking-policies" target="_blank">{chunks}</TextLink>,
              }}
            />
          </BodyText>
        </Group>}

      <MarkdownRender type="compact" fontSize="medium" content={finePrint} />
    </Group>
  )
}

export default FinePrint
